<template>
  <v-container>
    <h2> {{ $t("onetimePassword.title") }} | {{ merchantName }}</h2>
    <v-card-text v-if="isGenerating">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-2">{{ $t("onetimePassword.generating") }}</p>
    </v-card-text>

    <v-card-text v-else-if="onetimePassword.otp">
      <p class="">
        {{ $t("onetimePassword.yourCodeIs") }}：
      </p>
      <p class="onetime-code primary--text">
        {{ onetimePassword.otp }}
      </p>

      <div class="my-4">
        <img
          v-if="qrCodeDataUrl"
          :src="qrCodeDataUrl"
          alt="QR Code"
          class="qr-code-img"
        />
        <p v-else class="text-caption grey--text">
          {{ $t("onetimePassword.noQrCode") }}
        </p>
      </div>

      <!-- <p v-if="onetimePassword.otp && !hasExpired" class="caption timer">
        <span class="time"> {{ $t("onetimePassword.expirationDate") }}: </span> {{ formattedExpiryTime }}
      </p> -->

      <p 
        v-if="onetimePassword.otp"
        class="caption timer" 
        v-html="$t('onetimePassword.passwordExpiryMessage', { timeSpan: '<span class=&quot;time&quot;>' + formattedTimeLeft + '</span>' })"
      >
      </p>
      <p v-if="onetimePassword.otp && hasExpired" class="caption timer">
        <span class="time"> {{ $t("onetimePassword.expired") }} </span>
      </p>
  
      <div class="d-flex justify-center"> 
        <v-btn
          v-if="hasExpired"
          rounded
          color="primary"
          @click="reGenerateOnetimePassword"
          class="mt-4"
        >
          {{ $t("onetimePassword.reGenerate") }}
        </v-btn>
      </div>

      <!-- NFC Write Section -->
      <v-divider class="my-4" v-if="onetimePassword.otp"></v-divider>
      
      <div v-if="onetimePassword.otp" class="text-center">
        <h3 class="mb-3">NFC Tag Writing</h3>
        
        <!-- NFC Support Status -->
        <v-alert
          v-if="!isNfcSupported"
          type="warning"
          icon="mdi-nfc-variant-off"
          outlined
          class="mb-4"
        >
          NFC is not supported on this device or browser. Please use a compatible device with HTTPS.
        </v-alert>

        <!-- NFC Status Messages -->
        <v-alert
          v-if="nfcStatusMessage"
          :type="nfcStatusType"
          :icon="nfcStatusIcon"
          dismissible
          @input="nfcStatusMessage = ''"
          class="mb-4"
        >
          {{ nfcStatusMessage }}
        </v-alert>

        <!-- NFC Write Button -->
        <v-btn
          v-if="isNfcSupported && !isNfcWriting"
          rounded
          color="success"
          @click="writeToNfcTag"
          class="mt-2"
        >
          <v-icon left>mdi-nfc-variant</v-icon>
          Write OTP Tag via NFC
        </v-btn>

        <!-- NFC Writing in Progress -->
        <v-btn
          v-if="isNfcWriting"
          rounded
          color="warning"
          @click="abortNfcWrite"
          class="mt-2"
        >
          <v-progress-circular
            indeterminate
            size="20"
            class="mr-2"
          />
          Abort NFC Write
        </v-btn>

        <!-- NFC Success -->
        <v-btn
          v-if="nfcWriteSuccess"
          rounded
          color="success"
          disabled
          class="mt-2"
        >
          <v-icon left>mdi-check-circle</v-icon>
          NFC Write Successful!
        </v-btn>

        <!-- Instructions -->
        <p class="text-caption grey--text mt-3">
          Hold your NFC tag close to the device when prompted. The tag will be written with the OTP URL.
        </p>
      </div>
    </v-card-text>

    <v-card-text v-else>
       <p class="">
        {{ $t("onetimePassword.yourCodeIs") }}：
      </p>
      <p class="onetime-code primary--text"> {{ $t("onetimePassword.notGenerated") }} </p>
      <br/>
      <div class="d-flex justify-center"> 
        <v-btn rounded color="primary" @click="getOnetimePassword">
          {{ $t("onetimePassword.generate") }}
        </v-btn>
      </div>
    </v-card-text>
  </v-container>
</template>

<script>
// 將 import 語句更改為 require 語句，以解決 "Cannot use import statement outside a module" 錯誤
const axios = require("axios");
import QRCode from 'qrcode';

export default {
  name: "MerchantOnetimePassword",
  props: {
    merchantId: String,
    merchantName: String,
    merchant: Object
  },

  data() {
    return {
      isGenerating: false,
      onetimePassword: {
        code: null,
        otp: null, 
        expiresAt: null, // 到期時間戳 (Unix timestamp in milliseconds)
      },
      timeLeft: 0, // 剩餘時間 (秒)
      countdownInterval: null, // 計時器 ID
      qrCodeDataUrl: null, // QR Code 圖片的 Data URL
      
      // NFC related properties
      isNfcSupported: false,
      isNfcWriting: false,
      nfcWriteSuccess: false,
      nfcStatusMessage: '',
      nfcStatusType: 'info',
      nfcStatusIcon: 'mdi-information',
      nfcReader: null, // Store the NFC reader instance for aborting
    };
  },

  computed: {
    user() {
      return this.$store.getters.user;
    },
    organizations() {
      return this.$store.getters.organizations.map(org => {
        return {
          text: org["name"],
          value: org["id"]
        };
      });
    },
    // 格式化顯示的到期時間
    formattedExpiryTime() {
      if (!this.onetimePassword.expiresAt) return '';
      const date = new Date(this.onetimePassword.expiresAt * 1000);
      return date.toLocaleString(); // 顯示時分秒，可根據需要調整格式
    },
    // 格式化顯示剩餘時間
    formattedTimeLeft() {
      if (this.timeLeft <= 0) {
        return this.$t("onetimePassword.expired"); // 顯示過期訊息
      }
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
    // 檢查驗證碼是否已過期
    hasExpired() {
      return this.timeLeft <= 0 && this.onetimePassword.otp !== null;
    }
  },

  watch: {
    merchantId: {
      immediate: true,
      handler(mId) {
        if (mId) {
          // 確保 merchantId 存在
          this.init();
        }
      }
    },
    // 監聽 onetimePassword.code 變化，生成 QR Code
    'onetimePassword.code': {
      handler(newCode) {
        if (newCode) {
          this.generateQrCode(newCode);
        } else {
          this.qrCodeDataUrl = null; // 清空 QR Code
        }
      }
    }
  },

  methods: {
    /**
     * 初始化：取得驗證碼。
     */
    async init() {
      // 清理舊的狀態
      this.clearCountdown();
      this.onetimePassword.otp = null;
      this.onetimePassword.code = null;
      this.onetimePassword.expiresAt = null;
      this.timeLeft = 0;
      this.qrCodeDataUrl = null;

      await this.getOnetimePassword();
    },

    /**
     * 取得驗證碼
     */
    async getOnetimePassword() {
      this.isGenerating = true;
      try {
        // 替換為你的實際 API 端點和參數
        const response = await axios.post(
          `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/authenticator/otp`,
          {
            // 你可能需要傳遞使用者 token 或其他驗證資訊
            shopId: this.merchantId, // 假設 user 物件中有 id
          }
        );

        const { otp, token, exp } = response.data; // 假設 API 返回 code 和 expiresAt (Unix timestamp)
        this.onetimePassword.otp = otp;
        this.onetimePassword.code = token;
        this.onetimePassword.expiresAt = exp;

        this.startCountdown(); // 啟動倒數計時
      } catch (error) {
        console.error("Error fetching one-time password:", error);
        // this.onetimePassword.otp = "123";
        // this.onetimePassword.code = "13424"
        // this.onetimePassword.expiresAt = 1753771470;

        // 處理錯誤，例如顯示錯誤訊息給使用者
        this.$emit('snackbar', { message: 'Failed to get one-time password.', color: 'error' });
      } finally {
        this.isGenerating = false;
      }
    },

    /**
     * 啟動倒數計時器
     */
    startCountdown() {
      this.clearCountdown(); // 確保沒有重複的計時器

      const now = Date.now(); // 當前時間戳 (毫秒)
      this.timeLeft = Math.max(0, Math.floor((this.onetimePassword.expiresAt * 1000 - now) / 1000)); // 計算初始剩餘秒數

      if (this.timeLeft > 0) {
        this.countdownInterval = setInterval(() => {
          if (this.timeLeft > 0) {
            this.timeLeft--;
          } else {
            this.clearCountdown();
            // 在此可以觸發一些過期後的動作，例如顯示重新產生按鈕
            this.$emit('snackbar', { message: this.$t("onetimePassword.expired"), color: 'warning' });
          }
        }, 1000);
      }
    },

    /**
     * 清除倒數計時器
     */
    clearCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },

    /**
     * 重新產生驗證碼 (點擊按鈕觸發)
     */
    async reGenerateOnetimePassword() {
      await this.init(); // 重新執行初始化，獲取新的驗證碼
    },

    /**
     * 生成 QR Code
     * @param {string} text 要編碼的文字 (驗證碼)
     */
    async generateQrCode(text) {
      try {
        // 使用 toDataURL 生成圖片，可選 options 調整尺寸等
        this.qrCodeDataUrl = await QRCode.toDataURL(text, { width: 150, margin: 2,  errorCorrectionLevel: 'H' });
      } catch (err) {
        console.error('Error generating QR Code:', err);
        this.qrCodeDataUrl = null;
      }
    },

    /**
     * Check if NFC is supported on this device/browser
     */
    checkNfcSupport() {
      if ('NDEFReader' in window) {
        this.isNfcSupported = true;
      } else {
        this.isNfcSupported = false;
      }
    },

    /**
     * Fetch OTP tag URL from the API
     */
    async fetchOtpTagUrl() {
      try {
        const response = await axios.get(
          `/apis/api/merchants/${this.merchantId}/otp-tag-url`
          + `?platform=ddpay&user-id=ddpp.${this.$store.getters.user.id}`
        )
        return response.data.url || response.data;
      } catch (error) {
        console.error('Error fetching OTP tag URL:', error);
        throw new Error(`Failed to fetch OTP tag URL: ${error.response?.data?.message || error.message}`);
      }
    },

    /**
     * Write OTP URL to NFC tag
     */
    async writeToNfcTag() {
      if (!this.isNfcSupported) {
        this.showNfcStatus('NFC is not supported on this device', 'error', 'mdi-nfc-variant-off');
        return;
      }

      this.isNfcWriting = true;
      this.nfcWriteSuccess = false;
      this.nfcStatusMessage = '';

      try {
        // Fetch the OTP tag URL
        this.showNfcStatus('Fetching OTP tag URL...', 'info', 'mdi-download');
        const otpUrl = await this.fetchOtpTagUrl();
        
        if (!otpUrl) {
          throw new Error('No OTP URL received from server');
        }

        // Initialize NFC reader
        this.nfcReader = new NDEFReader();
        
        // Request permission to access NFC
        await this.nfcReader.scan();
        
        this.showNfcStatus('NFC ready. Please hold your tag close to the device...', 'info', 'mdi-nfc-variant');

        // Create NDEF message with the OTP URL
        const message = {
          records: [
            {
              recordType: 'url',
              data: otpUrl
            }
          ]
        };

        // Write to NFC tag
        await this.nfcReader.write(message);
        
        this.nfcWriteSuccess = true;
        this.showNfcStatus('Successfully wrote OTP URL to NFC tag!', 'success', 'mdi-check-circle');
        
      } catch (error) {
        console.error('NFC write error:', error);
        
        if (error.name === 'NotAllowedError') {
          this.showNfcStatus('NFC permission denied. Please allow NFC access.', 'error', 'mdi-lock');
        } else if (error.name === 'NotSupportedError') {
          this.showNfcStatus('NFC writing is not supported on this device.', 'error', 'mdi-nfc-variant-off');
        } else if (error.name === 'NotReadableError') {
          this.showNfcStatus('NFC tag is not readable or writable.', 'error', 'mdi-nfc-variant-off');
        } else if (error.name === 'InvalidStateError') {
          this.showNfcStatus('NFC is busy or in an invalid state.', 'error', 'mdi-nfc-variant-off');
        } else if (error.message.includes('Failed to fetch OTP tag URL')) {
          this.showNfcStatus(error.message, 'error', 'mdi-alert-circle');
        } else {
          this.showNfcStatus(`NFC write failed: ${error.message}`, 'error', 'mdi-alert-circle');
        }
      } finally {
        this.isNfcWriting = false;
        this.nfcReader = null;
      }
    },

    /**
     * Abort NFC write operation
     */
    abortNfcWrite() {
      if (this.nfcReader) {
        // Note: NDEFReader doesn't have a direct abort method, but we can reset our state
        this.nfcReader = null;
      }
      
      this.isNfcWriting = false;
      this.showNfcStatus('NFC write operation aborted', 'warning', 'mdi-cancel');
    },

    /**
     * Show NFC status message
     */
    showNfcStatus(message, type = 'info', icon = 'mdi-information') {
      this.nfcStatusMessage = message;
      this.nfcStatusType = type;
      this.nfcStatusIcon = icon;
    }
  },

  mounted() {
    // watch 首次載入時會觸發 init()，所以 mounted 不需要額外調用
    this.checkNfcSupport();
  },

  beforeDestroy() {
    this.clearCountdown(); // 組件銷毀前清除計時器，避免記憶體洩漏
  }
};
</script>

<style scoped>
.qr-code-img {
  max-width: 150px; /* 限制 QR Code 圖片大小 */
  height: auto;
  border: 1px solid #eee;
  padding: 5px;
}

.timer {
  font-size: 0.9em;
  color: #777;
}

.timer .time {
  font-weight: bold;
  color: #ff0000; /* 過期時間用紅色顯示 */
}

/* 新增的驗證碼樣式 */
.onetime-code {
  font-size: 2.5rem; /* 大幅放大字體 */
  font-weight: 900 !important; /* 加粗到最大程度 */
  letter-spacing: 0.1em; /* 增加字母間距，使其更容易閱讀 */
  margin-bottom: 15px; /* 與下方內容保持距離 */
}
</style>
